import React, { useEffect, useRef, useState } from "react";

/**
 * One-shot splash overlay that plays a short MP4 when the app mounts.
 * - Plays once per browser session (sessionStorage key `eplay_splash_seen`).
 * - Auto-dismisses after the video ends OR after SKIP_AFTER_MS (timeout safety).
 * - User can also tap/click to skip.
 * - Muted + playsInline so every browser autoplays without user gesture.
 * - Hard-skipped entirely if the session flag is already set or prefers-reduced-motion.
 */

const SESSION_KEY = "eplay_splash_seen";
const SKIP_AFTER_MS = 4500; // safety cap — matches the ~4s splash.mp4 duration
const PERSISTENT_KEY = "eplay_splash_seen_persistent";

/** Detect installed PWA / Android TWA / iOS standalone — these users skip the
 *  splash entirely. On a Play Store launch the user has already waited for the
 *  app icon → splash again would just look like a frozen black screen on slow
 *  Android WebViews where the video autoplay silently fails. */
function isStandaloneInstall() {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
    if (window.matchMedia && window.matchMedia("(display-mode: minimal-ui)").matches) return true;
    if (window.navigator && window.navigator.standalone === true) return true; // iOS
    // TWA referrer marker — Android's Chrome Custom Tab launches set this.
    if (typeof document !== "undefined" && document.referrer && document.referrer.startsWith("android-app://")) return true;
  } catch (e) { /* noop */ }
  return false;
}

export default function SplashScreen() {
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    // Hard-skip on installed PWA / TWA — no black screens for Play Store users.
    if (isStandaloneInstall()) return false;
    try {
      // Clear any legacy "seen forever" flags from older builds so the splash
      // is free to play on every full page reload from now on.
      window.localStorage.removeItem(PERSISTENT_KEY);
      window.sessionStorage.removeItem(SESSION_KEY);
      const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduce) return false;
    } catch (e) { /* noop */ }
    return true;
  });
  const [fading, setFading] = useState(false);
  const videoRef = useRef(null);

  // Attempt autoplay + safety timeout. Do NOT dismiss on play() rejection —
  // React 18 StrictMode's double-mount interrupts play with AbortError,
  // which would otherwise skip the splash instantly in development.
  // The <video onError> + onEnded + SKIP_AFTER_MS fallback already cover real failures.
  useEffect(() => {
    if (!visible) return undefined;
    const v = videoRef.current;
    if (v) {
      const p = v.play?.();
      if (p && typeof p.catch === "function") p.catch(() => { /* ignored on purpose */ });
    }
    const t = window.setTimeout(dismiss, SKIP_AFTER_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const dismiss = () => {
    // Don't persist any "seen" flag — option (a): replay the splash on every
    // full page reload. Within a single SPA session (React Router nav) the
    // component stays unmounted naturally once dismissed.
    setFading(true);
    window.setTimeout(() => setVisible(false), 350);
  };

  if (!visible) return null;
  return (
    <div
      data-testid="splash-screen"
      onClick={dismiss}
      onKeyDown={(e) => { if (e.key === "Escape" || e.key === "Enter" || e.key === " ") dismiss(); }}
      role="button"
      tabIndex={0}
      aria-label="ePlay intro — tap to skip"
      className={`fixed inset-0 z-[100] bg-black flex items-center justify-center transition-opacity duration-300 ${fading ? "opacity-0" : "opacity-100"}`}
    >
      <video
        ref={videoRef}
        data-testid="splash-video"
        src="/splash.mp4"
        poster="/icon-512.png"
        autoPlay
        muted
        playsInline
        preload="auto"
        onEnded={dismiss}
        onError={(e) => {
          // Any media error (decode/unsupported/network) → dismiss immediately,
          // don't trap users on a black screen waiting for the safety timeout.
          dismiss();
        }}
        onStalled={dismiss}
        onAbort={dismiss}
        className="max-w-full max-h-full w-auto h-auto"
      />
      <button
        data-testid="splash-skip"
        onClick={(e) => { e.stopPropagation(); dismiss(); }}
        className="absolute bottom-6 right-6 font-mono text-[10px] uppercase tracking-[0.35em] text-white/70 border border-white/40 px-4 py-2 hover:text-white hover:border-white transition-colors"
        aria-label="Skip intro"
      >
        Skip ›
      </button>
    </div>
  );
}
